// Tax estimates as region packs (spec §10, §63.4, §83). The core knows nothing about any country:
// a ruleset is data + one pure function, identified, dated and versioned, and every result says
// which ruleset produced it. Results are ESTIMATES for advisory conversations, never tax advice.
//
// CY-2026-v1 — sources: PwC Cyprus "Tax Facts & Figures 2026" (bands from 1 Jan 2026; life premiums
// deductible up to 7% of the sum insured; life + social insurance + GHS + pension/provident + medical
// fund together capped at 1/5 of chargeable income; child allowance €1,000 / €1,250 / €1,500 with
// household-income limits €100k/€150k/€200k; housing interest/rent up to €2,000). Contribution rates
// (social insurance 8.8%, GHS 2.65%) and the insurable-earnings ceiling are parameters the advisor
// can overwrite with the client's actual payslip figures.

export interface TaxBand { upTo: number | null; rate: number }
export interface TaxRuleset {
  id: string; region: string; year: number; label: string; sourceNote: string; verifiedAt: string;
  bands: TaxBand[];
  params: Record<string, number>;
}
export interface TaxInput {
  grossIncome: number;
  /** actual contributions if known; otherwise estimated from the parameters */
  socialInsurance?: number | null; ghs?: number | null;
  pensionFund?: number | null; providentFund?: number | null; medicalFund?: number | null;
  lifePolicies?: { annualPremium: number; sumInsured?: number | null }[];
  children?: number; householdIncome?: number | null; singleParent?: boolean;
  housingInterestOrRent?: number | null;
  /** 0.6: a single person without children (housing relief income limit €40,000 instead of the family limits) */
  single?: boolean;
  /** 0.7: employment-related expenses, incl. trade-union / professional-body subscriptions — deductible in full,
   *  outside the 1/5 cap (PwC Worldwide Tax Summaries, Cyprus individual deductions, reviewed 4 Aug 2026) */
  employmentExpenses?: number | null;
}
export interface TaxEstimate {
  rulesetId: string; year: number; estimated: true;
  chargeableIncome: number; cap: number;
  used: { socialInsurance: number; ghs: number; pension: number; provident: number; medical: number; life: number; total: number };
  capacity: number;
  allowances: { children: number; housing: number; employment?: number };
  taxBefore: number; taxAfter: number; saving: number; marginalRate: number;
  /** what an extra annual premium equal to the remaining capacity would save at the marginal rate */
  capacitySaving: number;
  notes: string[];
}

export const TAX_RULESETS: TaxRuleset[] = [
  {
    id: 'CY-2026-v2', region: 'CY', year: 2026, label: 'Cyprus — personal income tax 2026 (estimate)',
    sourceNote: 'PwC Cyprus, Tax Facts & Figures 2026; Cyprus tax reform effective 1 January 2026; Social Insurance Services announcement of 22 Dec 2025 (maximum insurable earnings €68,904 per year, via KPMG Cyprus tax alert).', verifiedAt: '2026-10-01',
    bands: [{ upTo: 22000, rate: 0 }, { upTo: 32000, rate: 0.2 }, { upTo: 42000, rate: 0.25 }, { upTo: 72000, rate: 0.3 }, { upTo: null, rate: 0.35 }],
    params: {
      reliefCapShare: 0.2, lifePremiumMaxShareOfSumInsured: 0.07, medicalFundMaxShare: 0.02, pensionFundMaxShare: 0.1,
      socialInsuranceRate: 0.088, socialInsuranceCeiling: 68904, ghsRate: 0.0265, ghsCeiling: 180000,
      child1: 1000, child2: 1250, child3plus: 1500, childLimit0to2: 100000, childLimit3to4: 150000, childLimit5plus: 200000, housingMax: 2000,
    },
  },
  // v1 is kept unchanged so saved calculations stay reproducible (its insurable-earnings ceiling was the 2025 figure).
  // Among provisional built-ins for the same year the most recently verified pack is used (tax-center.ts).
  {
    id: 'CY-2026-v1', region: 'CY', year: 2026, label: 'Cyprus — personal income tax 2026 (estimate)',
    sourceNote: 'PwC Cyprus, Tax Facts & Figures 2026; Cyprus tax reform effective 1 January 2026.', verifiedAt: '2026-09-30',
    bands: [{ upTo: 22000, rate: 0 }, { upTo: 32000, rate: 0.2 }, { upTo: 42000, rate: 0.25 }, { upTo: 72000, rate: 0.3 }, { upTo: null, rate: 0.35 }],
    params: {
      reliefCapShare: 0.2, lifePremiumMaxShareOfSumInsured: 0.07, medicalFundMaxShare: 0.02, pensionFundMaxShare: 0.1,
      socialInsuranceRate: 0.088, socialInsuranceCeiling: 66612, ghsRate: 0.0265, ghsCeiling: 180000,
      child1: 1000, child2: 1250, child3plus: 1500, childLimit0to2: 100000, childLimit3to4: 150000, childLimit5plus: 200000, housingMax: 2000,
    },
  },
];

export function taxRuleset(region?: string | null, year?: number): TaxRuleset | null {
  const list = TAX_RULESETS.filter((r) => r.region === (region ?? '').toUpperCase());
  if (!list.length) return null;
  return (year ? list.find((r) => r.year === year) : undefined) ?? list.sort((a, b) => b.year - a.year)[0]!;
}

export function taxOnIncome(income: number, bands: TaxBand[]): { tax: number; marginal: number } {
  let tax = 0, prev = 0, marginal = 0;
  for (const b of bands) {
    const top = b.upTo ?? Infinity;
    if (income > prev) { const slice = Math.min(income, top) - prev; tax += slice * b.rate; marginal = b.rate; }
    prev = top;
  }
  return { tax: Math.round(tax * 100) / 100, marginal };
}

const r2 = (n: number) => Math.round(n * 100) / 100;
const pos = (n: unknown) => (Number.isFinite(Number(n)) && Number(n) > 0 ? Number(n) : 0);

export function estimateTax(rs: TaxRuleset, input: TaxInput, overrides: Partial<Record<string, number>> = {}): TaxEstimate {
  const P = { ...rs.params, ...Object.fromEntries(Object.entries(overrides).filter(([, x]) => Number.isFinite(x))) } as Record<string, number>;
  const gross = pos(input.grossIncome);
  const notes: string[] = [];
  const si = input.socialInsurance != null ? pos(input.socialInsurance) : r2(Math.min(gross, P.socialInsuranceCeiling!) * P.socialInsuranceRate!);
  if (input.socialInsurance == null) notes.push('tax.note.si_estimated');
  const ghs = input.ghs != null ? pos(input.ghs) : r2(Math.min(gross, P.ghsCeiling!) * P.ghsRate!);
  // 0.7: pension AND provident fund contributions together are restricted to 10% of remuneration (PwC WWTS Cyprus,
  // individual deductions, reviewed 4 Aug 2026); before 0.7 only the pension fund was limited
  const pfLimit = gross * P.pensionFundMaxShare!;
  const pension = Math.min(pos(input.pensionFund), pfLimit);
  const provident = Math.min(pos(input.providentFund), Math.max(0, pfLimit - pension));
  if (pos(input.pensionFund) + pos(input.providentFund) > pfLimit + 0.005) notes.push('tax.note.provident_limited');
  const medical = Math.min(pos(input.medicalFund), gross * P.medicalFundMaxShare!);
  let life = 0;
  for (const lp of input.lifePolicies ?? []) {
    const cap = lp.sumInsured ? pos(lp.sumInsured) * P.lifePremiumMaxShareOfSumInsured! : pos(lp.annualPremium);
    if (!lp.sumInsured) notes.push('tax.note.life_no_sum_insured');
    life += Math.min(pos(lp.annualPremium), cap);
  }
  const cap = r2(gross * P.reliefCapShare!);
  const rawTotal = si + ghs + pension + provident + medical + life;
  const total = Math.min(rawTotal, cap);
  if (rawTotal > cap) notes.push('tax.note.cap_reached');
  // allowances (household-income tested)
  const kids = Math.max(0, Math.floor(input.children ?? 0));
  const limit = kids >= 5 ? P.childLimit5plus! : kids >= 3 ? P.childLimit3to4! : P.childLimit0to2!;
  let childAllowance = 0;
  if (kids && (input.householdIncome == null || input.householdIncome <= limit)) {
    for (let i = 1; i <= kids; i++) childAllowance += i === 1 ? P.child1! : i === 2 ? P.child2! : P.child3plus!;
    if (input.singleParent) childAllowance *= 2;
    if (input.householdIncome == null) notes.push('tax.note.household_income_assumed');
  }
  // housing interest/rent relief is household-income tested like the child allowance (0.6 fix: it was not)
  const housingLimit = input.single && !kids ? (P.singleIncomeLimit ?? 40000) : limit;
  const housingAllowed = input.householdIncome == null || input.householdIncome <= housingLimit;
  if (pos(input.housingInterestOrRent) > 0 && !housingAllowed) notes.push('tax.note.housing_over_threshold');
  if (pos(input.housingInterestOrRent) > 0 && input.householdIncome == null && !kids) notes.push('tax.note.household_income_assumed');
  const housing = housingAllowed ? Math.min(pos(input.housingInterestOrRent), P.housingMax!) : 0;
  const before = taxOnIncome(gross, rs.bands);
  const employment = pos(input.employmentExpenses);
  const chargeable = Math.max(0, gross - total - childAllowance - housing - employment);
  const after = taxOnIncome(chargeable, rs.bands);
  const capacity = r2(Math.max(0, cap - rawTotal));
  return {
    rulesetId: rs.id, year: rs.year, estimated: true, chargeableIncome: r2(chargeable), cap,
    used: { socialInsurance: r2(si), ghs: r2(ghs), pension: r2(pension), provident: r2(provident), medical: r2(medical), life: r2(life), total: r2(total) },
    capacity, allowances: { children: childAllowance, housing, employment: r2(employment) },
    taxBefore: before.tax, taxAfter: after.tax, saving: r2(before.tax - after.tax), marginalRate: after.marginal,
    capacitySaving: r2(capacity * after.marginal), notes: [...new Set(notes)],
  };
}
